"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractionPayloadSchema = exports.extractionContainerSchema = void 0;
const zod_1 = require("zod");
// Wire shape for an AI extraction of a Bill-of-Lading PDF. Mirrors
// `editableExtractionShape` in the backend's `update-extraction.schema.ts`
// field-for-field — except for the `*_weight_unit` fields, which are
// extraction-only: they are consumed when normalizing weights to metric
// tonnes during persistence and never reach the DB or the Edit form. The
// normalized weight columns drop straight into the `bl_extraction` row.
//
// Decimal-safe wire shape: dates are `YYYY-MM-DD` strings, weights are
// numeric strings (Prisma `Decimal` round-trips losslessly as a string),
// counts are finite numbers. Every field is value-or-null and defaults to
// null — a field the model omits is treated as "not found", not a hard
// validation failure (the all-null payload is still a valid extraction).
//
// Neither schema is `.strict()` — Zod's default (silently strip unknown
// keys) is the right shape here. Gemini sometimes emits stray keys the
// tool schema didn't request; with `.strict()` a single stray key would
// bucket the whole call as `invalid_response` and force the operator to
// re-extract. Stripping unknown keys keeps the known fields validated
// strictly while letting the recoverable case actually recover.
const dateOrNull = zod_1.z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date in YYYY-MM-DD form')
    .nullable()
    .default(null);
const stringOrNull = zod_1.z.string().nullable().default(null);
const numberOrNull = zod_1.z.number().finite().nullable().default(null);
// Weights are Decimal in the DB — keep as strings end-to-end to avoid
// floating-point rounding.
const decimalStringOrNull = zod_1.z.string().nullable().default(null);
// Unit-of-measure for a weight, captured verbatim as printed on the BL (e.g.
// "KGS", "MT", "T", "LBS"). A single BL routinely states the same weight in
// two units — a summary line in MT and the container line in KGS — so each
// weight carries its OWN unit and the model never converts. Normalization to
// metric tonnes happens deterministically in the backend persistence layer
// (`weight-normalization.ts`); these unit fields are consumed there and never
// stored, so they are extraction-only and intentionally absent from
// `editableExtractionShape` (operators edit the already-normalized MT value).
const weightUnitOrNull = stringOrNull;
exports.extractionContainerSchema = zod_1.z.object({
    container_number: stringOrNull,
    seal_number: stringOrNull,
    container_size: stringOrNull,
    net_weight: decimalStringOrNull,
    net_weight_unit: weightUnitOrNull,
    gross_weight: decimalStringOrNull,
    gross_weight_unit: weightUnitOrNull,
});
exports.extractionPayloadSchema = zod_1.z.object({
    bl_number: stringOrNull,
    bl_issue_date: dateOrNull,
    loading_date: dateOrNull,
    onboard_date: dateOrNull,
    vessel_name: stringOrNull,
    shipper: stringOrNull,
    shipping_line: stringOrNull,
    country_of_origin: stringOrNull,
    port_of_loading: stringOrNull,
    port_of_discharge: stringOrNull,
    final_destination: stringOrNull,
    product_description: stringOrNull,
    description_of_goods: stringOrNull,
    // HS code is a 6-10 digit identifier — keep as string to preserve leading
    // zeros and avoid floating-point rounding on long codes.
    hs_code: stringOrNull,
    applicable_free_days: numberOrNull,
    number_of_packages: numberOrNull,
    number_of_containers: numberOrNull,
    total_net_weight: decimalStringOrNull,
    total_net_weight_unit: weightUnitOrNull,
    total_gross_weight: decimalStringOrNull,
    total_gross_weight_unit: weightUnitOrNull,
    consignee_name: stringOrNull,
    consignee_address: stringOrNull,
    consignee_contact_person_name: stringOrNull,
    consignee_contact_person_email: stringOrNull,
    notify_name: stringOrNull,
    notify_address: stringOrNull,
    notify_contact_person_name: stringOrNull,
    notify_contact_person_email: stringOrNull,
    containers: zod_1.z.array(exports.extractionContainerSchema).default([]),
});
//# sourceMappingURL=extraction-payload.js.map